import React, { useCallback } from "react";
import { commitMutation, graphql, QueryRenderer, type RelayRefetchProp } from "react-relay";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faBolt } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce, noop } from "lodash";

import { ReactComponent as SalesforceLogo } from "~/legacy-assets/images/logos/salesforce.svg";
import { promptSalesforceAuth } from "~/lib/auth/index";
import { smart } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button, Tooltip } from "~/ui";
import { Modal, Switch } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import Settings from "../../../account/Integrations/Settings";
import { Label, Left, Right, Row, SectionTitle, SubLabel } from "../../SettingsModal/shared";
import { CRM_USER_SETTINGS_UPDATED_EVENT } from "../utils";
import { aboutInstalled } from "./SalesforceText";

import type {
	SalesforceInstanceType,
	SalesforceIntegration_viewer$data as SalesforcIntegrationViewer,
} from "~/__generated__/SalesforceIntegration_viewer.graphql";
import type { SalesforceIntegrationDisconnectMutation as DisconnectSalesforceType } from "~/__generated__/SalesforceIntegrationDisconnectMutation.graphql";
import type { SalesforceIntegrationQuery } from "~/__generated__/SalesforceIntegrationQuery.graphql";
import type {
	SalesforceUpdateSettingsInput,
	SalesforceIntegrationUpdateSettingsMutation as UpdateSettingsType,
} from "~/__generated__/SalesforceIntegrationUpdateSettingsMutation.graphql";

import settingsStyles from "~/settings/css/settings.module.css";

import styles from "./Salesforce.module.css";

const DisconnectSalesforceMutation = graphql`
	mutation SalesforceIntegrationDisconnectMutation($input: DisconnectSalesforceInput!) {
		disconnectSalesforce(input: $input) {
			ok
		}
	}
`;

interface DisconnectModalProps {
	onClose: () => void;
	relay: RelayRefetchProp;
}

const DisconnectModal: React.FunctionComponent<DisconnectModalProps> = props => {
	const { onClose, relay } = props;

	const onDisconnect = React.useCallback(() => {
		commitMutation<DisconnectSalesforceType>(environment, {
			mutation: DisconnectSalesforceMutation,
			variables: {
				input: {},
			},
			onCompleted: ({ disconnectSalesforce }) => {
				if (disconnectSalesforce?.ok) {
					toast.success(`Salesforce has been disconnected.`);
				} else {
					toast.error(`Sorry, something went wrong and we couldn't disconnect Salesforce.`);
				}
				relay.refetch({});
			},
		});

		onClose();
	}, [onClose, relay]);

	return (
		<Modal onClose={onClose} width="31.25em">
			<Modal.Header noBottomBorder onClose={onClose}>
				<Modal.Title>Are you sure you want to disconnect Salesforce?</Modal.Title>
			</Modal.Header>
			<Modal.Body textOnly className={styles.removeIntegrationModalBody}>
				Disconnecting Salesforce will cause your notes to no longer have have access to Salesforce.
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="destructive" onClick={onDisconnect}>
					Disconnect
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export type SalesforceSettingsProps = {
	onBackClick?: () => void;
};

type SalesforceSettingsInternalProps = SalesforceSettingsProps & {
	viewer: SalesforcIntegrationViewer;
	relay: RelayRefetchProp;
};

type SalesforceSettingsState = {
	autoSendNotesEnabled: boolean;
	includeRecapInAutoSendNotes: boolean;
	createInSalesforceAs: SalesforceInstanceType;
	relateToOpportunity: boolean;
	relateToAccount: boolean;
};

interface CreateInSalesforceAsOption extends SelectorOption {
	value: SalesforceInstanceType;
}

const createInSalesforceAsOptions: readonly CreateInSalesforceAsOption[] = [
	{
		label: "Call (Task)",
		value: "TASK",
	},
	{
		label: "Note",
		value: "NOTE",
	},
];

interface RelateToOption extends SelectorOption {
	value: "OPPORTUNITY" | "ACCOUNT";
}

const relateToOptions: readonly RelateToOption[] = [
	{
		label: "the Account",
		value: "ACCOUNT",
	},
	{
		label: "an Opportunity",
		value: "OPPORTUNITY",
	},
];

class SalesforceSettings extends React.Component<SalesforceSettingsInternalProps, SalesforceSettingsState> {
	static Fragments = {
		viewer: graphql`
			fragment SalesforceIntegration_viewer on Viewer {
				hasAccessToCopilot
				account {
					billingAccount {
						hasAccessToSalesforceFeature: hasAccessToFeature(feature: salesforce_integration)
					}
					integrations {
						hubspot {
							userConnected
						}
						salesforce {
							configurer {
								id
							}
						}
					}
				}
				integrations {
					salesforce {
						isConnected
						autoSendNotesEnabled
						includeRecapInAutoSendNotes
						createInSalesforceAs
						relateToOpportunity
						relateToAccount
					}
					hubspot {
						isConnected
					}
				}
			}
		`,
	};

	static Refetch = graphql`
		query SalesforceIntegrationRefetchQuery {
			viewer {
				...SalesforceIntegration_viewer
			}
		}
	`;

	script: HTMLScriptElement | undefined = undefined;

	constructor(props: SalesforceSettingsInternalProps) {
		super(props);
		this.state = {
			autoSendNotesEnabled: Boolean(props.viewer.integrations.salesforce?.autoSendNotesEnabled),
			includeRecapInAutoSendNotes: Boolean(props.viewer.integrations.salesforce?.includeRecapInAutoSendNotes),
			createInSalesforceAs: props.viewer.integrations.salesforce?.createInSalesforceAs || "TASK",
			relateToOpportunity: Boolean(props.viewer.integrations.salesforce?.relateToOpportunity),
			relateToAccount: Boolean(props.viewer.integrations.salesforce?.relateToAccount),
		};
	}

	onTabClick = noop;

	onDisconnectModalOpen = () => {
		__globalModalContext?.open(({ close }) => <DisconnectModal onClose={close} relay={this.props.relay} />);
	};

	onConnect = async () => {
		return promptSalesforceAuth({ refresh: true })
			.then(() => {
				this.props.relay.refetch(
					{},
					null,
					() => {
						const {
							autoSendNotesEnabled,
							includeRecapInAutoSendNotes,
							createInSalesforceAs,
							relateToOpportunity,
							relateToAccount,
						} = this.props.viewer.integrations.salesforce || {};

						this.setState({
							autoSendNotesEnabled: Boolean(autoSendNotesEnabled),
							includeRecapInAutoSendNotes: Boolean(includeRecapInAutoSendNotes),
							createInSalesforceAs: createInSalesforceAs || "TASK",
							relateToOpportunity: Boolean(relateToOpportunity),
							relateToAccount: Boolean(relateToAccount),
						});
					},
					{ force: true },
				);
				return toast.success(`Connected Salesforce`);
			})
			.catch(function ({ message }) {
				return toast.error(message);
			});
	};

	setAutoSendNotes = (autoSendNotesEnabled: boolean) => {
		this.setState({ autoSendNotesEnabled }, this.debouncedSaveSettings);
	};

	setIncludeRecapInAutoSendNotes = (includeRecapInAutoSendNotes: boolean) => {
		this.setState({ includeRecapInAutoSendNotes }, this.debouncedSaveSettings);
	};

	setCreateInSalesforceAs = (option: SingleValue<CreateInSalesforceAsOption>) => {
		if (!option) return;

		if (option.value === "TASK") {
			// If the user selects "Call (Task)", we should default to relating to the account
			this.setState(
				{ createInSalesforceAs: "TASK", relateToAccount: true, relateToOpportunity: false },
				this.debouncedSaveSettings,
			);
		} else if (option.value === "NOTE") {
			// If the user selects "Note", we should default to relating to both
			this.setState(
				{ createInSalesforceAs: "NOTE", relateToAccount: true, relateToOpportunity: true },
				this.debouncedSaveSettings,
			);
		}
	};

	setRelateTo = (option: SingleValue<RelateToOption>) => {
		if (!option) return;

		if (option.value === "OPPORTUNITY") {
			this.setState({ relateToOpportunity: true, relateToAccount: false }, this.debouncedSaveSettings);
		} else if (option.value === "ACCOUNT") {
			this.setState({ relateToOpportunity: false, relateToAccount: true }, this.debouncedSaveSettings);
		}
	};

	setRelateToOpportunity = (relateToOpportunity: boolean) => {
		this.setState({ relateToOpportunity }, this.debouncedSaveSettings);
	};

	setRelateToAccount = (relateToAccount: boolean) => {
		this.setState({ relateToAccount }, this.debouncedSaveSettings);
	};

	saveSettings = () => {
		const {
			autoSendNotesEnabled,
			includeRecapInAutoSendNotes,
			createInSalesforceAs,
			relateToOpportunity,
			relateToAccount,
		} = this.state;
		const input: SalesforceUpdateSettingsInput = {
			autoSendNotesEnabled,
			includeRecapInAutoSendNotes,
			createInSalesforceAs,
			relateToOpportunity,
			relateToAccount,
		};

		commitMutation<UpdateSettingsType>(environment, {
			mutation: graphql`
				mutation SalesforceIntegrationUpdateSettingsMutation($input: SalesforceUpdateSettingsInput!) {
					salesforceUpdateSettings(input: $input) {
						ok
					}
				}
			`,
			variables: {
				input,
			},
			onCompleted: ({ salesforceUpdateSettings }) => {
				if (salesforceUpdateSettings?.ok) {
					window.dispatchEvent(new Event(CRM_USER_SETTINGS_UPDATED_EVENT));
				}
			},
		});
	};

	debouncedSaveSettings = debounce(this.saveSettings, 500);

	render() {
		const { integrations, account, hasAccessToCopilot } = this.props.viewer;
		const connected = Boolean(integrations.salesforce?.isConnected);
		const paywalled = !account.billingAccount.hasAccessToSalesforceFeature;
		const automationAvailable = connected && !paywalled;

		// include other CRMs here
		const otherCRMConnected = !!integrations.hubspot?.isConnected || !!account.integrations.hubspot?.userConnected;

		const {
			autoSendNotesEnabled,
			includeRecapInAutoSendNotes,
			createInSalesforceAs,
			relateToOpportunity,
			relateToAccount,
		} = this.state;

		const createAsTaskSelected = createInSalesforceAs == "TASK";
		const createAsNoteSelected = createInSalesforceAs == "NOTE";

		return (
			<React.Fragment>
				<Settings
					name="Salesforce"
					connected={connected}
					logo={<SalesforceLogo height="24px" style={{ color: "#00A1E0" }} />}
					paywalledButton={paywalled ? "fellow_business_ai" : undefined}
					paywalledBanner={paywalled ? "fellow_business_ai" : undefined}
					paywallFeature="salesforce_integration"
					installText="Connect"
					tabs={[]}
					activeTab={""}
					onTabClick={this.onTabClick}
					onInstall={this.onConnect}
					onDisconnect={this.onDisconnectModalOpen}
					backPath="/settings/integrations/"
					installIcon={faBolt}
					disabledMessage={
						otherCRMConnected ? "You can only have one CRM integration connected at a given time." : undefined
					}
					// Custom Salesforce setup help article instead of the generic INTEGRATIONS_HELP_ARTICLE_ID one
					articleIdOnPaywall="5659554"
					onBackClick={this.props.onBackClick}
					showMoreActions={!paywalled}
				>
					{aboutInstalled}
					<section className={cx({ [styles.sectionDisabled]: !automationAvailable })}>
						<SectionTitle>Configurations</SectionTitle>
						<Row>
							<Left>
								<Label>Automatically send notes to Salesforce 3hrs after a meeting</Label>
							</Left>
							<Right>
								<Switch
									size="small"
									checked={autoSendNotesEnabled}
									onCheckedChange={this.setAutoSendNotes}
									disabled={!automationAvailable}
								/>
							</Right>
						</Row>
						<section className={cx(styles.subSettings, { [styles.sectionDisabled]: !autoSendNotesEnabled })}>
							{hasAccessToCopilot && (
								<Row>
									<span className={settingsStyles.indentArrowImage} />
									<Left>
										<Label>Include AI recap and link to recording</Label>
									</Left>
									<Right>
										<Switch
											size="small"
											disabled={!autoSendNotesEnabled || !automationAvailable}
											checked={includeRecapInAutoSendNotes}
											onCheckedChange={this.setIncludeRecapInAutoSendNotes}
										/>
									</Right>
								</Row>
							)}
							<Row>
								<span className={settingsStyles.indentArrowImage} />
								<Left>
									<Label>Create in Salesforce as a:</Label>
									{createAsTaskSelected && (
										<SubLabel>Call (Task) will be related to one of the contacts on the event.</SubLabel>
									)}
									{createAsNoteSelected && <SubLabel>Note will be related to contacts on the event.</SubLabel>}
								</Left>
								<Right>
									<Selector
										isDisabled={!autoSendNotesEnabled || !automationAvailable}
										condensed
										options={createInSalesforceAsOptions}
										value={createInSalesforceAsOptions.find(option => option.value === createInSalesforceAs)}
										onChange={this.setCreateInSalesforceAs}
										menuPlacement="top"
									/>
								</Right>
							</Row>
							{createAsTaskSelected && (
								<section className={cx(styles.subSettings, styles.indented)}>
									<Row>
										<span className={settingsStyles.indentArrowImage} />
										<Left>
											<Label>
												Relate to{" "}
												<Selector
													isDisabled={!autoSendNotesEnabled || !automationAvailable}
													condensed
													options={relateToOptions}
													value={relateToOptions.find(
														option =>
															(option.value === "OPPORTUNITY" && relateToOpportunity) ||
															(option.value === "ACCOUNT" && relateToAccount),
													)}
													onChange={this.setRelateTo}
													menuPlacement="top"
												/>
												<Tooltip
													content={
														relateToAccount
															? "Account related to contacts on the event"
															: "Will be related to one of the open opportunities related to the contact on the event"
													}
													placement="top"
													className={styles.tooltip}
													maxWidth="24ch"
												>
													<FontAwesomeIcon icon={faInfoCircle} size="sm" className={styles.tooltipIcon} />
												</Tooltip>
											</Label>
										</Left>
									</Row>
								</section>
							)}
							{createAsNoteSelected && (
								<section className={cx(styles.subSettings, styles.indented)}>
									<Row>
										<span className={settingsStyles.indentArrowImage} />
										<Left>
											<Label>
												Relate to Opportunities
												<Tooltip
													content={"Opportunities related to contacts on the event"}
													placement="top"
													className={styles.tooltip}
													maxWidth="24ch"
												>
													<FontAwesomeIcon icon={faInfoCircle} size="sm" className={styles.tooltipIcon} />
												</Tooltip>
											</Label>
										</Left>
										<Right>
											<Switch
												size="small"
												disabled={!autoSendNotesEnabled || !automationAvailable}
												checked={relateToOpportunity}
												onCheckedChange={this.setRelateToOpportunity}
											/>
										</Right>
									</Row>
									<Row>
										<span className={settingsStyles.indentArrowImage} />
										<Left>
											<Label>
												Relate to the Account
												<Tooltip
													content={"Account related to the contact on the event"}
													placement="top"
													className={styles.tooltip}
													maxWidth="24ch"
												>
													<FontAwesomeIcon icon={faInfoCircle} size="sm" className={styles.tooltipIcon} />
												</Tooltip>
											</Label>
										</Left>
										<Right>
											<Switch
												size="small"
												disabled={!autoSendNotesEnabled || !automationAvailable}
												checked={relateToAccount}
												onCheckedChange={this.setRelateToAccount}
											/>
										</Right>
									</Row>
								</section>
							)}
						</section>
					</section>
				</Settings>
			</React.Fragment>
		);
	}
}

const SmartSalesforceSettings = smart(SalesforceSettings);

function SalesforceIntegrationRenderer(props: SalesforceSettingsProps) {
	return (
		<QueryRenderer<SalesforceIntegrationQuery>
			environment={environment}
			query={graphql`
				query SalesforceIntegrationQuery {
					viewer {
						...SalesforceIntegration_viewer
					}
				}
			`}
			variables={{}}
			render={useCallback(
				({ props: data }) => (data ? <SmartSalesforceSettings {...data} {...props} /> : null),
				[props],
			)}
		/>
	);
}

export default SalesforceIntegrationRenderer;
